import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { MetricWidgetContext } from "./MetricWidgetContext";
import type { IChartDataItem } from "./types";
import { useCompactChartData } from "./useCompactChartData";

interface IMetricWidgetProviderProps {
    chartData?: { data: IChartDataItem[] }[] | null;
    isLoading: boolean;
    isError: boolean;
    children: ReactNode;
}

export const MetricWidgetProvider = ({ chartData, isLoading, isError, children }: IMetricWidgetProviderProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fullData = chartData?.[0]?.data;
    const { compactData, hasOthers } = useCompactChartData(fullData);
    const compactChartData = useMemo(() => (compactData.length > 0 ? [{ data: compactData }] : null), [compactData]);

    const openModal = useCallback(() => setIsModalOpen(true), []);
    const closeModal = useCallback(() => setIsModalOpen(false), []);

    const value = useMemo(
        () => ({
            state: { fullData, compactData, compactChartData, hasOthers, isLoading, isError, isModalOpen },
            actions: { openModal, closeModal },
        }),
        [fullData, compactData, compactChartData, hasOthers, isLoading, isError, isModalOpen, openModal, closeModal],
    );

    return <MetricWidgetContext.Provider value={value}>{children}</MetricWidgetContext.Provider>;
};
